import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Megaphone, Plus, X, Send, Printer } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { k: "construction_stop", labelEn: "Construction Stop", labelHi: "निर्माण रोक" },
  { k: "sanitation", labelEn: "Sanitation / Dirtiness", labelHi: "सफाई" },
  { k: "encroachment", labelEn: "Encroachment", labelHi: "अतिक्रमण" },
  { k: "show_cause", labelEn: "Show Cause", labelHi: "कारण बताओ" },
  { k: "tax_demand", labelEn: "Tax Demand", labelHi: "कर मांग" },
];

export default function Notices() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("construction_stop");
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    kind: "construction_stop", addressee_name: "", addressee_address: "",
    mobile: "", subject: "", body: "",
    issued_on: new Date().toISOString().slice(0, 10), response_due_days: 7,
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = { kind: tab };
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/notices", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [tab, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/notices", {
        panchayat_id: user?.panchayat_id, ...form, kind: tab,
        response_due_days: parseInt(form.response_due_days),
      });
      toast.success("Notice issued");
      setShow(false);
      setForm({ ...form, addressee_name: "", addressee_address: "", subject: "", body: "" });
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const sendSms = async (n) => {
    if (!n.mobile) { toast.error("No mobile on file"); return; }
    try {
      await api.post("/sms/send", {
        panchayat_id: n.panchayat_id, mobile: n.mobile,
        template_key: "custom", language: lang === "hi" ? "hi" : "en",
        context: { body: `Notice ${n.notice_no}: ${n.subject}` },
        ref_type: "notice", ref_id: n.id,
      });
      toast.success("SMS sent (DEMO)");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Notices</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "सूचना मॉड्यूल" : "Notice Module"}
            </h1>
          </div>
          <button onClick={() => setShow(true)} data-testid="notice-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
            <Plus className="w-4 h-4" /> {lang === "hi" ? "नई सूचना" : "New Notice"}
          </button>
        </div>

        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {KINDS.map((k) => (
            <button key={k.k} onClick={() => setTab(k.k)} data-testid={`notice-tab-${k.k}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === k.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>
              {lang === "hi" ? k.labelHi : k.labelEn}
            </button>
          ))}
        </div>

        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="notices-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr><th className="text-left px-4 py-3">Notice No.</th><th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Addressee</th><th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3 font-mono text-xs">{n.notice_no}</td>
                  <td className="px-4 py-3 text-xs">{n.issued_on}</td>
                  <td className="px-4 py-3"><div className="font-medium">{n.addressee_name}</div><div className="text-xs text-slate-500">{n.addressee_address}</div></td>
                  <td className="px-4 py-3 max-w-md"><div className="font-medium">{n.subject}</div><div className="text-xs text-slate-500 line-clamp-2">{n.body}</div></td>
                  <td className="px-4 py-3 capitalize text-xs">{n.status}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => sendSms(n)} data-testid={`notice-sms-${n.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-[#E87722] text-[#E87722]"><Send className="w-3 h-3" /> SMS</button>
                    <button onClick={() => window.print()} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-[#0A3161] text-[#0A3161]"><Printer className="w-3 h-3" /> Print</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No notices</td></tr>}
            </tbody>
          </table>
        </div>

        {show && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="font-heading text-xl font-bold">New {KINDS.find((k) => k.k === tab)?.labelEn} Notice</h2>
                <button onClick={() => setShow(false)} className="p-2 text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submit} className="p-5 space-y-3 text-sm">
                <Field label="Addressee Name"><input required value={form.addressee_name} onChange={(e) => setForm({ ...form, addressee_name: e.target.value })} className="form-input" data-testid="notice-name" /></Field>
                <Field label="Address"><input required value={form.addressee_address} onChange={(e) => setForm({ ...form, addressee_address: e.target.value })} className="form-input" /></Field>
                <Field label="Mobile"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="form-input" /></Field>
                <Field label="Subject"><input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="form-input" data-testid="notice-subj" /></Field>
                <Field label="Body"><textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="form-input" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Issue Date"><input type="date" required value={form.issued_on} onChange={(e) => setForm({ ...form, issued_on: e.target.value })} className="form-input" /></Field>
                  <Field label="Response Due (days)"><input type="number" required value={form.response_due_days} onChange={(e) => setForm({ ...form, response_due_days: e.target.value })} className="form-input" /></Field>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="notice-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>{busy ? "…" : "Issue Notice"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <style>{`.form-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.375rem;border:1px solid #CBD5E1;background:white;font-size:0.875rem;outline:none}.form-input:focus{border-color:#0A3161;box-shadow:0 0 0 3px rgba(10,49,97,0.08)}`}</style>
    </OfficerLayout>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{label}</div>{children}</div>
);
